import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, MessageSquare, Gavel, TrendingDown, Info, CheckCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TYPE_META = {
    inquiry:    { icon: MessageSquare,  color: "text-blue-600",    bg: "bg-blue-500/10" },
    auction:    { icon: Gavel,          color: "text-orange-600",  bg: "bg-orange-500/10" },
    price_drop: { icon: TrendingDown,   color: "text-emerald-600", bg: "bg-emerald-500/10" },
    system:     { icon: Info,           color: "text-navy-500",    bg: "bg-navy-500/10" },
};

// Shared refresh signal — any component can dispatch this to force all mounted bells to re-poll.
export const NOTIF_REFRESH_EVENT = "gpm:notifications:refresh";
export const emitNotifRefresh = () => {
    if (typeof window !== "undefined") window.dispatchEvent(new Event(NOTIF_REFRESH_EVENT));
};

const timeAgo = (iso) => {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
};

export default function NotificationBell({ variant = "desktop" }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [loading, setLoading] = useState(false);
    const wrapRef = useRef(null);

    // Poll unread-count every 30s while logged in; also refresh on cross-component signal.
    useEffect(() => {
        if (!user) { setUnread(0); return; }
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/notifications/unread-count");
                if (!cancelled) setUnread(data?.count || 0);
            } catch { /* silent */ }
        };
        load();
        const id = setInterval(load, 30000);
        const onRefresh = () => load();
        window.addEventListener(NOTIF_REFRESH_EVENT, onRefresh);
        return () => {
            cancelled = true;
            clearInterval(id);
            window.removeEventListener(NOTIF_REFRESH_EVENT, onRefresh);
        };
    }, [user]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/notifications?limit=10");
            setItems(Array.isArray(data) ? data : []);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, []);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next) fetchItems();
    };

    const onItemClick = async (n) => {
        try {
            if (!n.read) {
                await api.patch(`/notifications/${n.id}/read`);
                setUnread((u) => Math.max(0, u - 1));
                setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
                emitNotifRefresh();
            }
        } catch { /* silent */ }
        setOpen(false);
        if (n.link) nav(n.link);
    };

    const markAllRead = async () => {
        try {
            const { data } = await api.post("/notifications/read-all");
            if (data?.updated !== undefined) {
                setUnread(0);
                setItems((prev) => prev.map((x) => ({ ...x, read: true })));
                emitNotifRefresh();
            }
        } catch { /* silent */ }
    };

    if (!user) return null;

    const btnBase = variant === "mobile"
        ? "relative p-2 text-white hover:text-gold-500"
        : "relative p-2 text-white/85 hover:text-gold-500";

    return (
        <div ref={wrapRef} className="relative">
            <button
                type="button"
                onClick={toggle}
                aria-label="Notifications"
                data-testid="notif-bell-btn"
                className={btnBase}
            >
                <Bell className="w-5 h-5" />
                {unread > 0 && (
                    <span
                        data-testid="notif-bell-badge"
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-navy-900"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    data-testid="notif-panel"
                    className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-16 sm:top-auto sm:mt-2 sm:w-[360px] max-w-[calc(100vw-1rem)] bg-white rounded-xl shadow-2xl border border-navy-500/10 z-50 overflow-hidden"
                >
                    <div className="px-4 py-3 border-b border-navy-500/10 flex items-center justify-between bg-navy-500/[0.03]">
                        <div className="font-heading font-bold text-navy-500 text-sm">Notifications</div>
                        <button
                            onClick={markAllRead}
                            data-testid="notif-mark-all-read"
                            className="text-[11px] uppercase tracking-wider font-bold text-gold-700 hover:text-gold-500 inline-flex items-center gap-1"
                        >
                            <CheckCheck className="w-3 h-3" /> Mark all read
                        </button>
                    </div>

                    <div className="max-h-[420px] overflow-y-auto" data-testid="notif-list">
                        {loading && (
                            <div className="px-4 py-8 text-center text-navy-500/60 text-sm">Loading…</div>
                        )}
                        {!loading && items.length === 0 && (
                            <div className="px-4 py-10 text-center">
                                <Bell className="w-8 h-8 mx-auto text-navy-500/20 mb-2" />
                                <div className="text-sm text-navy-500/60">You&apos;re all caught up!</div>
                                <div className="text-[11px] text-navy-500/40 mt-1">New inquiries, price drops and auction alerts will show here.</div>
                            </div>
                        )}
                        {!loading && items.map((n) => {
                            const meta = TYPE_META[n.type] || TYPE_META.system;
                            const Icon = meta.icon;
                            return (
                                <button
                                    key={n.id}
                                    onClick={() => onItemClick(n)}
                                    data-testid={`notif-item-${n.id}`}
                                    className={`w-full text-left px-4 py-3 border-b border-navy-500/5 hover:bg-gold-500/5 flex gap-3 transition-colors ${n.read ? "opacity-70" : ""}`}
                                >
                                    <div className={`shrink-0 w-9 h-9 rounded-full ${meta.bg} flex items-center justify-center`}>
                                        <Icon className={`w-4 h-4 ${meta.color}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start gap-2">
                                            <div className={`text-sm ${n.read ? "font-medium" : "font-bold"} text-navy-500 line-clamp-2`}>
                                                {n.title}
                                            </div>
                                            {!n.read && <span className="mt-1 w-2 h-2 rounded-full bg-gold-500 shrink-0" />}
                                        </div>
                                        {n.body && (
                                            <div className="text-xs text-navy-500/70 mt-0.5 line-clamp-2">{n.body}</div>
                                        )}
                                        <div className="text-[10px] uppercase tracking-wider text-navy-500/40 mt-1">
                                            {timeAgo(n.created_at)}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <Link
                        to="/notifications"
                        onClick={() => setOpen(false)}
                        data-testid="notif-view-all"
                        className="block text-center px-4 py-2.5 bg-navy-500/[0.03] text-navy-500 text-xs font-bold uppercase tracking-wider hover:bg-gold-500/10 hover:text-gold-700 border-t border-navy-500/10"
                    >
                        View all notifications →
                    </Link>
                </div>
            )}
        </div>
    );
}
